import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Avatar,
  Chip,
  Button,
  Breadcrumbs,
  Link,
  Divider,
  TextField,
  IconButton,
  useTheme,
  useMediaQuery,
  Card,
  CardMedia,
  CardContent,
  Rating,
  Skeleton
} from '@mui/material';
import { motion } from 'framer-motion';
import { useLanguage } from '../Component/LanguageProvider';
import { useNavigate, useParams } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import ShareIcon from '@mui/icons-material/Share';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import CommentIcon from '@mui/icons-material/Comment';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PrintIcon from '@mui/icons-material/Print';
import GavelIcon from '@mui/icons-material/Gavel';
import BusinessIcon from '@mui/icons-material/Business';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DescriptionIcon from '@mui/icons-material/Description';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

export const BlogDetailsPage = () => {
  const { currentLang, direction } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [liked, setLiked] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const [comment, setComment] = useState('');
  const [comments, setComments] = useState([]);

  // دوال مساعدة للـ Avatar
  const getInitials = (name) => {
    const nameParts = name.split(' ');
    if (nameParts.length >= 2) {
      // خذ الحرف الأول من الاسم الأول فقط
      return nameParts[0][0].toUpperCase();
    }
    // لو اسم واحد، خذ أول حرف فقط
    return name.substring(0, 1).toUpperCase();
  };

  const maskSecondName = (name) => {
    const nameParts = name.split(' ');
    if (nameParts.length >= 2) {
      // الاسم الأول كامل + الحرف الأول من الاسم الثاني + نقاط
      const firstName = nameParts[0];
      const secondNameFirstLetter = nameParts[1][0];
      return `${firstName} ${secondNameFirstLetter}.`;
    }
    return name;
  };

  // لون موحد للـ Avatar - أزرق ثابت
  const AVATAR_COLOR = '#4361ee';

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  const floatingAnimation = {
    y: [0, -20, 0],
    transition: {
      duration: 5,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  // بيانات المقالات
  const blogPosts = {
    en: [
      {
        id: 1,
        title: 'Understanding UAE Labor Law: A Comprehensive Guide',
        excerpt: 'Learn about the key aspects of UAE labor law, including employee rights, contract types, and termination procedures.',
        content: `The UAE Labor Law is designed to protect both employers and employees, ensuring a fair and productive working environment. This comprehensive guide covers:

## 1. Employment Contracts
Fixed-term and unlimited contracts explained in detail. Learn about the differences between them and which one suits your needs.

## 2. Working Hours
Standard hours, overtime, and flexible arrangements. Understand your rights regarding working hours and overtime pay.

## 3. Leave Entitlements
Annual, sick, and special leave provisions. Know how much leave you're entitled to and how to request it.

## 4. Termination
Legal procedures and end-of-service benefits. Learn about the proper way to terminate employment contracts and what benefits you're entitled to.

## 5. Dispute Resolution
How to handle workplace conflicts. Understand the legal procedures for resolving disputes between employers and employees.

Understanding these regulations is crucial for both employers and employees to maintain compliance and avoid legal issues.`,
        image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        category: 'Labor Law',
        categoryId: 'labor-law',
        date: '2024-03-15',
        readTime: '8 min',
        author: 'Ahmed Al Mansoori',
        authorBio: 'Senior Legal Consultant with over 15 years of experience in UAE labor law. Ahmed has helped hundreds of companies navigate complex employment regulations.',
        views: 1234,
        likes: 89,
        comments: 23,
        tags: ['UAE Law', 'Employment', 'Contracts']
      },
      {
        id: 2,
        title: 'Starting a Business in Dubai: Legal Requirements',
        excerpt: 'A step-by-step guide to understanding the legal framework for setting up a business in Dubai and the UAE.',
        content: `Dubai offers tremendous opportunities for entrepreneurs, but navigating the legal requirements can be complex. This guide walks you through:

## 1. Business Structures
Mainland vs. Free Zone vs. Offshore. Understand the differences between each structure and which one is right for your business.

## 2. Licensing Requirements
Commercial, industrial, and professional licenses. Learn what type of license you need and how to obtain it.

## 3. Visa Processes
Investor and employment visas. Understand the visa requirements for you and your employees.

## 4. Local Sponsorship
When and how it's required. Learn about the local sponsorship requirements for mainland companies.

## 5. Compliance
Ongoing legal obligations. Understand what you need to do to stay compliant after setting up your business.

Whether you're a local entrepreneur or foreign investor, understanding these requirements is essential for success.`,
        image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        category: 'Business Setup',
        categoryId: 'business-setup',
        date: '2024-03-10',
        readTime: '10 min',
        author: 'Sara Al Hashimi',
        authorBio: 'Business Setup Specialist with 10 years of experience helping entrepreneurs establish their businesses in Dubai.',
        views: 2156,
        likes: 156,
        comments: 34,
        tags: ['Dubai Courts', 'Free Zone', 'Mainland']
      },
      {
        id: 3,
        title: 'Dubai Rental Disputes: What Tenants and Landlords Need to Know',
        excerpt: 'Essential information about resolving rental disputes in Dubai through the Rental Dispute Settlement Center.',
        content: `Rental disputes are common in Dubai's dynamic real estate market. This article explains:

## 1. RERA and Ejari
Registration requirements and importance. Learn why registering your tenancy contract is crucial.

## 2. Rent Increases
Legal limits and notification periods. Understand how and when landlords can increase rent.

## 3. Maintenance Responsibilities
Who pays for what. Learn about the division of maintenance responsibilities between tenants and landlords.

## 4. Eviction Rules
Legal grounds and procedures. Understand when and how landlords can evict tenants.

## 5. Dispute Resolution
How to file a case at the Rental Dispute Center. Step-by-step guide to resolving rental disputes.

Both tenants and landlords benefit from understanding their rights and obligations under Dubai's rental laws.`,
        image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        category: 'Property Law',
        categoryId: 'property-law',
        date: '2024-03-05',
        readTime: '7 min',
        author: 'Mohammed Al Falasi',
        authorBio: 'Real Estate Legal Expert specializing in Dubai property law and rental disputes.',
        views: 1876,
        likes: 134,
        comments: 28,
        tags: ['Dubai Courts', 'Contracts', 'Legal Tips']
      }
    ],
    ar: [
      {
        id: 1,
        title: 'دليل شامل لقانون العمل الإماراتي',
        excerpt: 'تعرف على الجوانب الرئيسية لقانون العمل الإماراتي، بما في ذلك حقوق الموظفين وأنواع العقود وإجراءات إنهاء الخدمة.',
        content: `صُمم قانون العمل الإماراتي لحماية كل من أصحاب العمل والموظفين، مما يضمن بيئة عمل عادلة ومنتجة. يغطي هذا الدليل الشامل:

## ١. عقود العمل
شرح العقود محددة المدة وغير المحددة. تعرف على الفروقات بينهما وأيهما يناسب احتياجاتك.

## ٢. ساعات العمل
الساعات القياسية والعمل الإضافي والترتيبات المرنة. افهم حقوقك فيما يتعلق بساعات العمل وأجر العمل الإضافي.

## ٣. استحقاقات الإجازات
الإجازات السنوية والمرضية والخاصة. اعرف مقدار الإجازة التي تستحقها وكيفية طلبها.

## ٤. إنهاء الخدمة
الإجراءات القانونية ومكافآت نهاية الخدمة. تعرف على الطريقة الصحيحة لإنهاء عقود العمل والمكافآت التي تستحقها.

## ٥. تسوية المنازعات
كيفية التعامل مع النزاعات في مكان العمل. افهم الإجراءات القانونية لحل النزاعات بين أصحاب العمل والموظفين.

فهم هذه اللوائح أمر بالغ الأهمية لكل من أصحاب العمل والموظفين للحفاظ على الامتثال وتجنب المشكلات القانونية.`,
        image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        category: 'قانون العمل',
        categoryId: 'labor-law',
        date: '2024-03-15',
        readTime: '٨ دقائق',
        author: 'أحمد المنصوري',
        authorBio: 'مستشار قانوني أول بخبرة تزيد عن ١٥ عاماً في قانون العمل الإماراتي. ساعد أحمد المئات من الشركات في التعامل مع لوائح العمل المعقدة.',
        views: 1234,
        likes: 89,
        comments: 23,
        tags: ['قانون الإمارات', 'عمالة', 'عقود']
      },
      {
        id: 2,
        title: 'تأسيس شركة في دبي: المتطلبات القانونية',
        excerpt: 'دليل خطوة بخطوة لفهم الإطار القانوني لتأسيس شركة في دبي والإمارات.',
        content: `تقدم دبي فرصاً هائلة لرواد الأعمال، لكن فهم المتطلبات القانونية قد يكون معقداً. يرشدك هذا الدليل خلال:

## ١. هياكل الأعمال
البر الرئيسي vs. المناطق الحرة. افهم الفروقات بين كل هيكل وأيهما مناسب لعملك.

## ٢. متطلبات الترخيص
التراخيص التجارية والصناعية والمهنية. تعرف على نوع الترخيص الذي تحتاجه وكيفية الحصول عليه.

## ٣. إجراءات التأشيرات
تأشيرات المستثمرين وتأشيرات العمل. افهم متطلبات التأشيرة لك ولموظفيك.

## ٤. الكفالة المحلية
متى وكيف تكون مطلوبة. تعرف على متطلبات الكفالة المحلية لشركات البر الرئيسي.

## ٥. الامتثال
الالتزامات القانونية المستمرة. افهم ما عليك فعله للبقاء ملتزماً بعد تأسيس عملك.

سواء كنت رائد أعمال محلياً أو مستثمراً أجنبياً، فهم هذه المتطلبات ضروري للنجاح.`,
        image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        category: 'تأسيس الأعمال',
        categoryId: 'business-setup',
        date: '2024-03-10',
        readTime: '١٠ دقائق',
        author: 'سارة الهاشمي',
        authorBio: 'أخصائية تأسيس شركات بخبرة ١٠ سنوات في مساعدة رواد الأعمال على تأسيس أعمالهم في دبي.',
        views: 2156,
        likes: 156,
        comments: 34,
        tags: ['محاكم دبي', 'مناطق حرة', 'بر رئيسي']
      },
      {
        id: 3,
        title: 'منازعات الإيجار في دبي: ما يحتاج المستأجرون والمالكون معرفته',
        excerpt: 'معلومات أساسية حول حل المنازعات الإيجارية في دبي من خلال مركز فض المنازعات الإيجارية.',
        content: `المنازعات الإيجارية شائعة في سوق العقارات الديناميكي في دبي. يشرح هذا المقال:

## ١. مؤسسة التنظيم العقاري وإيجاري
متطلبات التسجيل وأهميتها. تعرف على أهمية تسجيل عقد الإيجار الخاص بك.

## ٢. زيادات الإيجار
الحدود القانونية وفترات الإخطار. افهم كيف ومتى يمكن للمالكين زيادة الإيجار.

## ٣. مسؤوليات الصيانة
من يدفع ماذا. تعرف على تقسيم مسؤوليات الصيانة بين المستأجرين والمالكين.

## ٤. قواعد الإخلاء
الأسباب والإجراءات القانونية. افهم متى وكيف يمكن للمالكين إخلاء المستأجرين.

## ٥. تسوية المنازعات
كيفية رفع قضية في مركز المنازعات الإيجارية. دليل خطوة بخطوة لحل النزاعات الإيجارية.

يستفيد كل من المستأجرين والمالكين من فهم حقوقهم والتزاماتهم بموجب قوانين الإيجار في دبي.`,
        image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        category: 'قانون العقارات',
        categoryId: 'property-law',
        date: '2024-03-05',
        readTime: '٧ دقائق',
        author: 'محمد الفلاسي',
        authorBio: 'خبير قانوني متخصص في قانون العقارات في دبي ومنازعات الإيجار.',
        views: 1876,
        likes: 134,
        comments: 28,
        tags: ['محاكم دبي', 'عقود', 'نصائح قانونية']
      }
    ]
  };

  // تحميل بيانات المقال
  useEffect(() => {
    setLoading(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    setTimeout(() => {
      const posts = blogPosts[currentLang];
      const foundPost = posts.find(p => p.id === parseInt(id));
      
      if (foundPost) {
        setPost(foundPost);
        
        const related = posts
          .filter(p => p.categoryId === foundPost.categoryId && p.id !== foundPost.id)
          .slice(0, 3);
        setRelatedPosts(related);
        
        setComments([
          {
            id: 1,
            name: 'Mohamed Ali',
            avatar: 'https://randomuser.me/api/portraits/men/5.jpg',
            date: '2024-03-16',
            content: 'Great article! Very informative and well-written. Thanks for sharing.',
            likes: 5
          },
          {
            id: 2,
            name: 'Fatima Hassan',
            avatar: 'https://randomuser.me/api/portraits/women/6.jpg',
            date: '2024-03-15',
            content: 'This helped me understand my rights as an employee. Much appreciated!',
            likes: 3
          }
        ]);
      }
      
      setLoading(false);
    }, 1000);
  }, [id, currentLang]);

  // مشاركة المقال
  const handleShare = (platform) => {
    const url = window.location.href;
    const text = post?.title;
    
    switch(platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank');
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
        break;
      case 'whatsapp':
        window.open(`https://api.whatsapp.com/send?text=${text} ${url}`, '_blank');
        break;
      default:
        break;
    }
  };

  // طباعة المقال
  const handlePrint = () => {
    window.print();
  };

  // إرسال تعليق
  const handleCommentSubmit = (e) => {
    e.preventDefault();
    if (comment.trim()) {
      const newComment = {
        id: comments.length + 1,
        name: currentLang === 'ar' ? 'زائر' : 'Guest',
        avatar: 'https://randomuser.me/api/portraits/lego/1.jpg',
        date: new Date().toISOString().split('T')[0],
        content: comment,
        likes: 0
      };
      setComments([newComment, ...comments]);
      setComment('');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: '20px', mb: 4 }} />
        <Skeleton variant="text" height={60} sx={{ mb: 2 }} />
        <Skeleton variant="text" height={30} sx={{ mb: 1 }} />
        <Skeleton variant="text" height={30} sx={{ mb: 1 }} />
        <Skeleton variant="text" height={30} sx={{ mb: 4 }} />
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: '15px', mb: 4 }} />
      </Container>
    );
  }

  if (!post) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom>
          {currentLang === 'ar' ? 'المقال غير موجود' : 'Article Not Found'}
        </Typography>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/blogs')}
          sx={{ mt: 3, bgcolor: '#667eea' }}
        >
          {currentLang === 'ar' ? 'العودة للمدونة' : 'Back to Blog'}
        </Button>
      </Container>
    );
  }

  return (
    <>
      {/* <Navbarsection /> */}
      
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: '60vh', md: '80vh' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          color: 'white',
          mb: { xs: 4, md: 8 },
          backgroundImage: `url(${post.image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(102,126,234,0.8) 100%)',
            zIndex: 1
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Breadcrumbs 
              sx={{ 
                justifyContent: 'center', 
                display: 'flex',
                mb: 3,
                '& .MuiBreadcrumbs-separator': { color: 'rgba(255,255,255,0.5)' }
              }}
            >
              <Link
                component="button"
                onClick={() => navigate('/')}
                sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white' } }}
              >
                <HomeIcon sx={{ fontSize: 16, mr: 0.5 }} />
                {currentLang === 'ar' ? 'الرئيسية' : 'Home'}
              </Link>
              <Link
                component="button"
                onClick={() => navigate('/blogs')}
                sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white' } }}
              >
                {currentLang === 'ar' ? 'المدونة' : 'Blog'}
              </Link>
              <Typography sx={{ color: 'white' }}>
                {post.category}
              </Typography>
            </Breadcrumbs>

            <Chip
              label={post.category}
              sx={{ bgcolor: '#667eea', color: 'white', mb: 3 }}
            />

            <Typography
              variant="h2"
              sx={{
                fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                fontWeight: 800,
                fontSize: { xs: '1.8rem', md: '3rem' },
                textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                maxWidth: '900px',
                mx: 'auto'
              }}
            >
              {post.title}
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ pb: 8 }}>
        <Grid container spacing={4}>
          {/* Main Article */}
          <Grid item xs={12} md={8}>
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {/* Author Info - مع Avatar نصي */}
              <motion.div variants={itemVariants}>
                <Paper sx={{ p: 3, mb: 4, borderRadius: '15px' }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item>
                      <Avatar
                        sx={{
                          width: 60,
                          height: 60,
                          bgcolor: AVATAR_COLOR,
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '1.2rem'
                        }}
                      >
                        {getInitials(post.author)}
                      </Avatar>
                    </Grid>
                    <Grid item xs>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {maskSecondName(post.author)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {post.authorBio}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CalendarTodayIcon sx={{ fontSize: 16, color: '#999' }} />
                          <Typography variant="caption">{post.date}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <AccessTimeIcon sx={{ fontSize: 16, color: '#999' }} />
                          <Typography variant="caption">{post.readTime}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <RemoveRedEyeIcon sx={{ fontSize: 16, color: '#999' }} />
                          <Typography variant="caption">{post.views} views</Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </motion.div>

              {/* Article Content */}
              <motion.div variants={itemVariants}>
                <Paper sx={{ p: { xs: 3, md: 5 }, mb: 4, borderRadius: '20px' }}>
                  <Typography
                    component="div"
                    sx={{
                      fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                      lineHeight: 1.8,
                      fontSize: '1.1rem',
                      '& h1': { fontSize: '2rem', fontWeight: 'bold', my: 3 },
                      '& h2': { fontSize: '1.5rem', fontWeight: 'bold', my: 2 },
                      '& h3': { fontSize: '1.2rem', fontWeight: 'bold', my: 2 },
                      '& p': { mb: 2 },
                      '& ul, & ol': { mb: 2, pl: 4 }
                    }}
                  >
                    {post.content.split('\n').map((paragraph, idx) => {
                      if (paragraph.startsWith('##')) {
                        return <Typography key={idx} variant="h5" sx={{ fontWeight: 'bold', my: 3 }}>{paragraph.replace('##', '')}</Typography>;
                      } else if (paragraph.startsWith('-')) {
                        return <li key={idx}>{paragraph.replace('-', '')}</li>;
                      } else if (paragraph.trim()) {
                        return <Typography key={idx} paragraph>{paragraph}</Typography>;
                      }
                      return null;
                    })}
                  </Typography>

                  {/* Tags */}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 4 }}>
                    {post.tags.map((tag, idx) => (
                      <Chip
                        key={idx}
                        label={tag}
                        size="small"
                        sx={{ bgcolor: '#f0f0f0' }}
                      />
                    ))}
                  </Box>

                  {/* Share & Actions */}
                  <Divider sx={{ my: 4 }} />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        onClick={() => setLiked(!liked)}
                        sx={{ color: liked ? '#e83e8c' : '#999' }}
                      >
                        {liked ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                      </IconButton>
                      <IconButton
                        onClick={() => setBookmarked(!bookmarked)}
                        sx={{ color: bookmarked ? '#667eea' : '#999' }}
                      >
                        {bookmarked ? <BookmarkIcon /> : <BookmarkBorderIcon />}
                      </IconButton>
                      <IconButton onClick={handlePrint} sx={{ color: '#999' }}>
                        <PrintIcon />
                      </IconButton>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Typography variant="body2" sx={{ color: '#999', alignSelf: 'center' }}>
                        {currentLang === 'ar' ? 'مشاركة:' : 'Share:'}
                      </Typography>
                      <IconButton onClick={() => handleShare('facebook')} sx={{ color: '#1877F2' }}>
                        <FacebookIcon />
                      </IconButton>
                      <IconButton onClick={() => handleShare('twitter')} sx={{ color: '#1DA1F2' }}>
                        <TwitterIcon />
                      </IconButton>
                      <IconButton onClick={() => handleShare('linkedin')} sx={{ color: '#0A66C2' }}>
                        <LinkedInIcon />
                      </IconButton>
                      <IconButton onClick={() => handleShare('whatsapp')} sx={{ color: '#25D366' }}>
                        <WhatsAppIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </Paper>
              </motion.div>

              {/* Comments Section */}
          {/* Comments Section - مع Avatar نصي للمعلقين */}
<motion.div variants={itemVariants}>
  <Paper sx={{ p: { xs: 3, md: 4 }, borderRadius: '20px' }}>
    <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3 }}>
      {currentLang === 'ar' ? `التعليقات (${comments.length})` : `Comments (${comments.length})`}
    </Typography>

    {/* Add Comment */}
    {/* <Box component="form" onSubmit={handleCommentSubmit} sx={{ mb: 4 }}>
      <TextField
        fullWidth
        multiline
        rows={3}
        placeholder={currentLang === 'ar' ? 'اكتب تعليقك...' : 'Write your comment...'}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        sx={{ mb: 2 }}
      />
      <Button
        type="submit"
        variant="contained"
        disabled={!comment.trim()}
        sx={{ bgcolor: '#667eea' }}
      >
        {currentLang === 'ar' ? 'إرسال التعليق' : 'Post Comment'}
      </Button>
    </Box> */}

    {/* Comments List */}
    {comments.map((cmt) => (
      <Box key={cmt.id} sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
          {/* Avatar نصي للتعليقات - نفس التنسيق */}
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: AVATAR_COLOR,
              color: 'white',
              fontWeight: 'bold',
              fontSize: '1rem'
            }}
          >
            {getInitials(cmt.name)}
          </Avatar>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              {maskSecondName(cmt.name)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {cmt.date}
            </Typography>
          </Box>
        </Box>
        <Typography variant="body2" sx={{ ml: 7, mb: 1 }}>
          {cmt.content}
        </Typography>
        <Box sx={{ ml: 7, display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton size="small">
            <FavoriteBorderIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Typography variant="caption">{cmt.likes}</Typography>
         
        </Box>
        <Divider sx={{ mt: 2 }} />
      </Box>
    ))}
  </Paper>
</motion.div>
            </motion.div>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            <motion.div variants={itemVariants} initial="hidden" animate="visible">
              {/* Related Posts */}
              <Paper sx={{ p: 3, borderRadius: '20px', mb: 4, position: 'sticky', top: 100 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
                  {currentLang === 'ar' ? 'مقالات ذات صلة' : 'Related Articles'}
                </Typography>

                {relatedPosts.length > 0 ? (
                  relatedPosts.map((related) => (
                    <Box
                      key={related.id}
                      sx={{ display: 'flex', gap: 2, mb: 2, cursor: 'pointer' }}
                      onClick={() => navigate(`/blog/${related.id}`)}
                    >
                      <Box
                        component="img"
                        src={related.image}
                        sx={{ width: 80, height: 60, borderRadius: '10px', objectFit: 'cover' }}
                      />
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                          {related.title.length > 60 ? related.title.substring(0, 60) + '...' : related.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {related.date}
                        </Typography>
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    {currentLang === 'ar' ? 'لا توجد مقالات ذات صلة' : 'No related articles'}
                  </Typography>
                )}

                <Divider sx={{ my: 3 }} />

                {/* Categories */}
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                  {currentLang === 'ar' ? 'التصنيفات' : 'Categories'}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {['Labor Law', 'Business Setup', 'Property Law', 'Digital Nomads'].map((cat, idx) => (
                    <Button
                      key={idx}
                      variant="text"
                      sx={{ justifyContent: 'flex-start', color: '#666', textTransform: 'none' }}
                      onClick={() => navigate('/blogs')}
                    >
                      {cat}
                    </Button>
                  ))}
                </Box>
              </Paper>
            </motion.div>
          </Grid>
        </Grid>

        {/* Back to Blog Button */}
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/blogs')}
            sx={{ borderColor: '#667eea', color: '#667eea' }}
          >
            {currentLang === 'ar' ? 'العودة للمدونة' : 'Back to Blog'}
          </Button>
        </Box>
      </Container>
    </>
  );
};

export default BlogDetailsPage;